// @mui
import LanguageIcon from "@mui/icons-material/Language"
import { MenuItem, Stack } from "@mui/material"
import Divider from "@mui/material/Divider"
import { alpha } from "@mui/material/styles"
import { useQueryClient } from "@tanstack/react-query"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { useRevalidator } from "react-router"

import IconButtonAnimate from "@sap/components/animate/IconButtonAnimate"
import Image from "@sap/components/image/Image"
import MenuPopover from "@sap/components/menu-popover/MenuPopover"

import { updateCurrentUserCurrency } from "@/api/index"
import allLangs from "@/locales/langs"
import type { LangsValuetype } from "@/locales/langs"
import { useAuthStore } from "@/store/auth"

export default function LocalizationPopover({ background }: { background?: boolean }) {
  const { i18n, t } = useTranslation()
  const revalidator = useRevalidator()
  const queryClient = useQueryClient()
  const { user } = useAuthStore((state) => state)

  const allowed_currency_codes = user?.allowed_currency_codes || []

  const [openPopover, setOpenPopover] = useState<HTMLButtonElement | null>(null)

  const handleOpenPopover = (event: React.MouseEvent<HTMLButtonElement>) => {
    setOpenPopover(event.currentTarget)
  }

  const handleClosePopover = () => {
    setOpenPopover(null)
  }

  const handleChangeLang = (newLang: LangsValuetype) => {
    i18n.changeLanguage(newLang)
    revalidator.revalidate()
    queryClient.resetQueries()
    handleClosePopover()
  }

  const handleChangeCurrency = async (newCurrency: string) => {
    await updateCurrentUserCurrency({ selected_currency_code: newCurrency })

    revalidator.revalidate()
    queryClient.resetQueries()
    handleClosePopover()
  }

  return (
    <>
      <IconButtonAnimate
        onClick={handleOpenPopover}
        sx={{
          width: 40,
          height: 40,
          ...(background && {
            backgroundColor: "grey.200",
          }),
          ...(openPopover && {
            "&:before": {
              zIndex: 1,
              content: "''",
              width: "100%",
              height: "100%",
              borderRadius: "50%",
              position: "absolute",
              bgcolor: (theme) => alpha(theme.palette.grey[600], 0.5),
            },
          }),
        }}
      >
        <LanguageIcon />
      </IconButtonAnimate>

      <MenuPopover
        open={openPopover}
        onClose={handleClosePopover}
        sx={{ minwidth: 180, zIndex: 600 }}
      >
        <Stack spacing={0.75}>
          {allLangs.map((option) => (
            <MenuItem
              key={option.value}
              selected={option.value === i18n.language}
              onClick={() => handleChangeLang(option.value)}
            >
              <Image
                disabledEffect
                alt={option.label}
                src={option.icon}
                sx={{ width: 28, mr: 2 }}
              />

              {option.label}
            </MenuItem>
          ))}
          {user?.is_staff && (
            <>
              <Divider variant="fullWidth" />
              {allowed_currency_codes.map((code) => (
                <MenuItem
                  key={code}
                  selected={user?.selected_currency_code === code}
                  onClick={() => handleChangeCurrency(code)}
                >
                  {t("currencies." + code)}
                </MenuItem>
              ))}
            </>
          )}
        </Stack>
      </MenuPopover>
    </>
  )
}
